
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this._priceSyncTimers = [];
    this._schedulePriceSync = () => {
      this._priceSyncTimers.forEach((id) => clearTimeout(id));
      this._priceSyncTimers = [];

      const isSimpleProduct = this.closest('.evamats-product-simple');
      const delays = isSimpleProduct ? [0] : [0, 120];

      delays.forEach((delay) => {
        const id = setTimeout(() => this.updatePrices(), delay);
        this._priceSyncTimers.push(id);
      });
    };

    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        this._schedulePriceSync();
      });
    }

    document.addEventListener('evamats:discount-updated', () => {
      this._schedulePriceSync();
    });

    window.addEventListener('load', () => {
      this._schedulePriceSync();
    });
  }

  getProductPriceScope() {
    return (
      this.closest('.product__info-wrapper') ||
      this.closest('.product') ||
      this.closest('[id^="shopify-section"]') ||
      document
    );
  }

  formatMoneyLikeDisplay(cents, referenceHTML) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = referenceHTML || '';
    const plain = (wrapper.textContent || '').trim();
    const currency = plain.replace(/[\d.,\s\u00A0-]/g, '').trim() || '€';
    const amount = Number(cents) / 100;
    if (!amount) return '';
    const locale = document.documentElement.lang || undefined;
    const rounded = Number(amount.toFixed(2));
    // «165,60 €», not «165,6 €»: an amount with cents keeps both decimals; whole amounts stay as they are («735 zł»).
    const decimals = Number.isInteger(rounded) ? 0 : 2;
    const formatted = rounded.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: 2 });
    return `${formatted} ${currency}`.trim();
  }

  formatSimpleVariantMoney(cents, referenceHTML) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return this.formatMoneyLikeDisplay(cents, referenceHTML);
  }

  getDisplayedProductPrices() {
    const simpleRoot = this.closest('.evamats-product-simple');
    if (simpleRoot) {
      if (!this.currentVariant) {
        this.updateOptions();
        this.updateMasterId();
      }
      if (this.currentVariant) {
        const reference =
          simpleRoot.querySelector('.evamats-config-checkout__price-current, .evamats-product-checkout__price-current')?.textContent || '';
        const saleHTML = this.formatSimpleVariantMoney(this.currentVariant.price, reference);
        let compareHTML = null;
        const compareAt = this.currentVariant.compare_at_price;
        if (compareAt && compareAt > this.currentVariant.price) {
          compareHTML = this.formatSimpleVariantMoney(compareAt, saleHTML);
        }
        return { saleHTML, compareHTML };
      }
    }

    const scope = this.getProductPriceScope();

    const variantPriceFallback = () => {
      if (!this.currentVariant) return { saleHTML: '', compareHTML: null };
      const reference = (
        document.querySelector('.sticky_config_price_price, .evamats-config-checkout__price-current, .evamats-product-checkout__price-current')?.textContent || ''
      )
        .replace(/\u00A0/g, ' ')
        .trim();
      const symbol = reference.replace(/[\d.,\s-]/g, '').trim();
      const symbolFirst = symbol ? reference.indexOf(symbol) === 0 : false;
      const hasDecimals = /[.,]\d{2}(?!\d)/.test(reference);
      const locale = document.documentElement.lang || undefined;
      const formatCents = (cents) => {
        const amount = Number(cents) / 100;
        if (!amount) return '';
        const formatted = amount.toLocaleString(locale, {
          minimumFractionDigits: hasDecimals ? 2 : 0,
          maximumFractionDigits: hasDecimals ? 2 : 0,
        });
        if (!symbol) return formatted;
        return symbolFirst ? `${symbol}${formatted}` : `${formatted} ${symbol}`;
      };
      const saleHTML = formatCents(this.currentVariant.price);
      let compareHTML = null;
      const compareAt = this.currentVariant.compare_at_price;
      if (compareAt && compareAt > this.currentVariant.price) {
        compareHTML = formatCents(compareAt);
      }
      return { saleHTML, compareHTML };
    };

    const evamatsRoot = scope.querySelector('[data-evamats-discount]:not([hidden])');
    if (evamatsRoot) {
      const saleEl = evamatsRoot.querySelector('[data-evamats-sale]');
      const compareEl = evamatsRoot.querySelector('[data-evamats-compare]');
      if (saleEl && saleEl.textContent.trim()) {
        return {
          saleHTML: saleEl.innerHTML,
          compareHTML: compareEl && compareEl.textContent.trim() ? compareEl.innerHTML : null,
        };
      }
    }

    const mainPrice = scope.querySelector('.main_price') || scope.querySelector('#google_prices');
    if (!mainPrice) {
      return variantPriceFallback();
    }

    const priceRoot = mainPrice.querySelector('.price') || mainPrice;
    const onSale = priceRoot.classList.contains('price--on-sale');
    const saleEl = onSale
      ? priceRoot.querySelector('.price-item--sale, .price-item--last')
      : priceRoot.querySelector('.price__regular .price-item--regular, .price-item--regular');
    const compareEl = onSale
      ? priceRoot.querySelector('.price__sale s.price-item--regular, .price__sale .price-item--regular')
      : null;

    if (!saleEl || !saleEl.textContent.trim()) {
      return variantPriceFallback();
    }

    return {
      saleHTML: saleEl.innerHTML,
      compareHTML: compareEl && compareEl.textContent.trim() ? compareEl.innerHTML : null,
    };
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    // this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      // this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      if (!this.closest('.config_container')) {
        this.updateMedia();
      }
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    mediaGalleries.forEach(mediaGallery => mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true));

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    if (!this.currentVariant) return;
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input =
        productForm.querySelector('input.product-variant-id[name="id"]') ||
        productForm.querySelector('input[name="id"]');
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const firstChecked = this.querySelector(':checked');
    if (!firstChecked || !this.variantData) return;
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => firstChecked.value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')]
      const prevChecked = inputWrappers[index - 1].querySelector(':checked');
      if (!prevChecked) return;
      const previousOptionSelected = prevChecked.value;
      const availableOptionInputsValue = selectedOptionOneVariants.filter(variant => variant.available && variant[`option${index}`] === previousOptionSelected).map(variantOption => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue)
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
  }

  updatePrices() {
    const stickyConfigPriceElement = document.querySelector('.sticky_container');
    const stickyConfigPriceElements = document.querySelectorAll('.sticky_config_price_price');
    const buttonsPrice = document.querySelectorAll('.button_price');
    const upsellItemsCheckboxes = document.querySelectorAll('.product__upsell_custom_checkbox input[type="checkbox"]:checked');

    let upsellTotal = 0;
    upsellItemsCheckboxes.forEach(checkbox => {
      upsellTotal += parseFloat(checkbox.dataset.price) / 100 || 0;
    });

    const { saleHTML, compareHTML } = this.getDisplayedProductPrices();
    if (!saleHTML) return;

    const simpleRoot = this.closest('.evamats-product-simple');

    const parsePriceFromHTML = (htmlString) => {
      const doc = document.createElement('div');
      doc.innerHTML = htmlString || '';
      let compact = (doc.textContent || '')
        .replace(/\u00A0/g, ' ')
        .replace(/\s/g, '')
        .replace(/[^\d.,-]/g, '');
      if (!compact) return 0;
      const neg = compact.startsWith('-');
      if (neg) compact = compact.slice(1);

      const lastDot = compact.lastIndexOf('.');
      const lastComma = compact.lastIndexOf(',');
      let num = 0;
      if (lastDot === -1 && lastComma === -1) {
        num = parseFloat(compact) || 0;
      } else if (lastDot > lastComma) {
        const afterSep = compact.slice(lastDot + 1);
        if (afterSep.length === 3 && /^\d{3}$/.test(afterSep) && lastDot <= 3) {
          num = parseFloat(compact.replace(/\./g, '')) || 0;
        } else {
          num = parseFloat(compact.replace(/,/g, '')) || 0;
        }
      } else {
        const afterSep = compact.slice(lastComma + 1);
        if (afterSep.length === 3 && /^\d{3}$/.test(afterSep)) {
          num = parseFloat(compact.replace(/,/g, '')) || 0;
        } else {
          num = parseFloat(compact.replace(/\./g, '').replace(',', '.')) || 0;
        }
      }
      return neg ? -num : num;
    };

    const getSumHTML = (htmlString, addValue) => {
      const numericValue = parsePriceFromHTML(htmlString);
      const wrapper = document.createElement('div');
      wrapper.innerHTML = htmlString || '';
      const plain = (wrapper.textContent || '').trim();
      const currency = plain.replace(/[\d.,\s\u00A0-]/g, '').trim() || '€';
      const sum = numericValue + addValue;
      const locale = document.documentElement.lang || undefined;
      const formatted = Number(sum.toFixed(2)).toLocaleString(locale, { maximumFractionDigits: 2 });
      return `${formatted} ${currency}`.trim();
    };

    const totalHTML = upsellTotal === 0 ? saleHTML : getSumHTML(saleHTML, upsellTotal);
    const buttonPrice = document.querySelectorAll('.product__info-wrapper .button_price');
    const optionPrice = document.querySelector('.option__price_regular');
    const optionPriceCompareEls = document.querySelectorAll('.option__price_compare');

    stickyConfigPriceElements.forEach((priceEl) => {
      priceEl.innerHTML = totalHTML;
    });
    buttonPrice.forEach((button) => {
      button.innerHTML = totalHTML;
    });
    buttonsPrice.forEach((button) => {
      button.innerHTML = totalHTML;
    });

    if (simpleRoot) {
      const simpleCompareEl = simpleRoot.querySelector('.evamats-config-checkout__price-old, .evamats-product-checkout__price-old');
      if (simpleCompareEl) {
        if (compareHTML) {
          simpleCompareEl.classList.remove('hidden');
          simpleCompareEl.innerHTML = compareHTML;
        } else {
          simpleCompareEl.classList.add('hidden');
          simpleCompareEl.innerHTML = '';
        }
      }
    }

    if (optionPrice) {
      optionPrice.innerHTML = saleHTML;
    }
    if (optionPriceCompareEls.length) {
      let fullPriceHTML = compareHTML;

      if (!fullPriceHTML && this.currentVariant?.price) {
        const saleNum = parsePriceFromHTML(saleHTML);
        const variantNum = this.currentVariant.price / 100;
        if (variantNum > saleNum) {
          const candidateHTML = this.formatMoneyLikeDisplay(this.currentVariant.price, saleHTML);
          const normalizePriceText = (html) =>
            (html || '')
              .replace(/<[^>]*>/g, '')
              .replace(/\u00A0/g, ' ')
              .replace(/\s/g, '')
              .trim();
          if (normalizePriceText(candidateHTML) !== normalizePriceText(saleHTML)) {
            fullPriceHTML = candidateHTML;
          }
        }
      }

      optionPriceCompareEls.forEach((optionPriceCompare) => {
        if (fullPriceHTML) {
          optionPriceCompare.classList.remove('hidden');
          optionPriceCompare.innerHTML = fullPriceHTML;
          optionPrice?.classList.add('option__price_regular--sale');
        } else {
          optionPriceCompare.classList.add('hidden');
          optionPriceCompare.innerHTML = '';
          optionPrice?.classList.remove('option__price_regular--sale');
        }
      });
    }

    document.dispatchEvent(new CustomEvent('evamats:price-updated'));

    if (stickyConfigPriceElement && !stickyConfigPriceElement.closest('.evamats-config')) {
      stickyConfigPriceElement.removeAttribute('style');
      stickyConfigPriceElement.style.width = stickyConfigPriceElement.offsetWidth + 1 + 'px';
    }
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const skuSource = html.getElementById(`Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(`Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('visibility-hidden');

        if (inventoryDestination) inventoryDestination.classList.toggle('visibility-hidden', inventorySource.innerText === '');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant
          }
        });
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
    if (inventory) inventory.classList.add('visibility-hidden');
    if (sku) sku.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
    this._onLabelCaptureClick = this._onLabelCaptureClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onLabelCaptureClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onLabelCaptureClick, true);
  }

  _onLabelCaptureClick(event) {
    const label = event.target.closest('label[for]');
    if (!label || !this.contains(label)) return;
    const forId = label.getAttribute('for');
    if (!forId) return;
    const input = document.getElementById(forId);
    if (!input || input.type !== 'radio' || !this.contains(input) || input.disabled) return;

    const ensure = () => {
      if (input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(ensure);
    } else {
      setTimeout(ensure, 0);
    }
  }

  applyVariantToRadioInputs(variant) {
    if (!variant || !variant.options || !variant.options.length) return;
    this.querySelectorAll('fieldset[data-option-position]').forEach((fieldset) => {
      const pos = parseInt(fieldset.dataset.optionPosition, 10);
      if (!Number.isFinite(pos) || pos < 1 || pos > variant.options.length) return;
      const target = variant.options[pos - 1];
      if (target == null) return;
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      let needsUpdate = false;
      radios.forEach((radio) => {
        const shouldCheck = radio.value === target;
        if (shouldCheck !== radio.checked) needsUpdate = true;
      });
      if (!needsUpdate) return;
      radios.forEach((radio) => {
        radio.checked = radio.value === target;
      });
    });
  }

  updateVariantInput() {
    super.updateVariantInput();
    if (this.currentVariant) {
      this.applyVariantToRadioInputs(this.currentVariant);
    }
  }

  updateMasterId() {
    const variantData = this.getVariantData();
    if (!variantData.length) {
      this.currentVariant = null;
      return;
    }
    const numOpts = variantData[0].options.length;
    const productForm = document.querySelector(`#product-form-${this.dataset.section}`);
    const idInput = productForm
      ? productForm.querySelector('input.product-variant-id[name="id"]') ||
        productForm.querySelector('input[name="id"]')
      : null;
    const prevVariant = idInput
      ? variantData.find((v) => String(v.id) === String(idInput.value))
      : null;

    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));

    const buildSelected = (fillGapsFromPrev) => {
      const sel = new Array(numOpts).fill(undefined);
      fieldsets.forEach((fieldset) => {
        const pos = parseInt(fieldset.dataset.optionPosition, 10);
        if (!Number.isFinite(pos) || pos < 1 || pos > numOpts) return;
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) sel[pos - 1] = checked.value;
      });
      if (fillGapsFromPrev) {
        for (let i = 0; i < numOpts; i++) {
          if (sel[i] === undefined && prevVariant && prevVariant.options[i] !== undefined) {
            sel[i] = prevVariant.options[i];
          }
        }
      }
      return sel;
    };

    const match = (sel) =>
      variantData.filter((variant) =>
        variant.options.every((opt, i) => sel[i] === undefined || sel[i] === opt)
      );

    let candidates = match(buildSelected(true));
    if (!candidates.length) {
      candidates = match(buildSelected(false));
    }
    this.currentVariant =
      candidates.find((v) => v.available) || candidates[0] || null;
  }

  updateVariantStatuses() {
    const variantData = this.variantData || this.getVariantData();
    if (!variantData.length) return;

    const numOpts = variantData[0].options.length;
    const positionFieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'))
      .map((fs) => ({
        fs,
        pos: parseInt(fs.dataset.optionPosition, 10),
      }))
      .filter((x) => Number.isFinite(x.pos) && x.pos >= 1 && x.pos <= numOpts)
      .sort((a, b) => a.pos - b.pos);

    if (!positionFieldsets.length) return;

    const firstChecked = positionFieldsets[0].fs.querySelector('input[type="radio"]:checked');
    if (!firstChecked) return;

    const selectedOptionOneVariants = variantData.filter(
      (variant) => firstChecked.value === variant.option1
    );

    for (let i = 1; i < positionFieldsets.length; i++) {
      const { fs, pos } = positionFieldsets[i];
      const prevFs = positionFieldsets[i - 1];
      const prevChecked = prevFs.fs.querySelector('input[type="radio"]:checked');
      if (!prevChecked) return;

      const previousOptionSelected = prevChecked.value;
      const prevPos = prevFs.pos;
      const optionInputs = [...fs.querySelectorAll('input[type="radio"], option')];
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter(
          (variant) =>
            variant.available && variant[`option${prevPos}`] === previousOptionSelected
        )
        .map((variant) => variant[`option${pos}`]);

      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    }
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
    this.options = fieldsets.map((fieldset) => {
      const checked = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
        (radio) => radio.checked
      );
      return checked ? checked.value : undefined;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

// reverse years in select on product
(function () {
  document.addEventListener("DOMContentLoaded", function() {
    const select = document.getElementById("rok-produkcji");
    if (select) {
        const preservedValue = select.value;
        // Берём все option, кроме первого
        const firstOption = select.options[0];
        const options = Array.from(select.options).slice(1);

        // Реверсим список годов
        options.reverse();

        // Чистим select и возвращаем первый option "Please select"
        select.innerHTML = "";
        select.appendChild(firstOption);

        // Добавляем обратно перевёрнутые года
        options.forEach(option => select.appendChild(option));

        if (preservedValue && Array.from(select.options).some((opt) => opt.value === preservedValue)) {
          select.value = preservedValue;
        } else {
          firstOption.selected = true;
        }
    }
  });
})();

// reverse years in select on product


// swiper cart notification

(function () {
    if (document.querySelector('.related_product__list')) {
        const cartSwiper = new Swiper('.related_product__list', {
            spaceBetween: 10,
            slidesPerView: 2,
            // If we need pagination
          
            // Navigation arrows
            navigation: {
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            },
            breakpoints: {
                800: {
                  slidesPerView: 2
                },
                1024: {
                  slidesPerView: 3
                },
              }
          });
    }
})();
// swiper cart notification

// toggle galleries on change mats type inputs
(function () {
    function initMatsTypeGalleryToggle() {
      const radioButtons = document.querySelectorAll('fieldset[data-name="mats_type"] input[type="radio"]');
      const mediaGalleryWithoutEdges = document.querySelector('.product__media_without_edges');
      const mediaGalleryWithEdges = document.querySelector('.product__media_with_edges');

      if (!radioButtons.length || !mediaGalleryWithoutEdges || !mediaGalleryWithEdges) return;

      const clickSecondImage = (index) => {
        const gallery = Number(index) === 1 ? mediaGalleryWithoutEdges : mediaGalleryWithEdges;
        const thumbnail = gallery.querySelectorAll('.thumbnail')[1];
        if (thumbnail) thumbnail.click();
      };

      const applyMatsTypeGallery = (index, selectSecondSlide) => {
        const idx = Number(index);
        if (idx === 1) {
          mediaGalleryWithoutEdges.classList.add('active');
          mediaGalleryWithEdges.classList.remove('active');
        } else if (idx === 2) {
          mediaGalleryWithoutEdges.classList.remove('active');
          mediaGalleryWithEdges.classList.add('active');
        } else {
          return;
        }

        if (selectSecondSlide) {
          requestAnimationFrame(() => clickSecondImage(idx));
        }
      };

      radioButtons.forEach((radio) => {
        radio.addEventListener('change', function () {
          applyMatsTypeGallery(this.dataset.index, true);
        });
      });

      const checkedRadio = document.querySelector('fieldset[data-name="mats_type"] input[type="radio"]:checked');
      if (checkedRadio) {
        applyMatsTypeGallery(checkedRadio.dataset.index, true);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initMatsTypeGalleryToggle);
    } else {
      initMatsTypeGalleryToggle();
    }
  })();
// toggle size product config mobile
(function () {
    const toggler = document.querySelector('.product__config__toggle_size')
    const container = document.querySelector('.sticky_config_price')
    const parentContainer = document.querySelector('.sticky_config_container')
    const image = document.querySelector('.product__config_image--mobile_image')
    if (toggler) {
      toggler.addEventListener('click', () => {
        toggler.classList.toggle('active')
        container.classList.toggle('wide')
        parentContainer.classList.toggle('wide')
        container.removeAttribute('style')
      })
      if (image) {
        image.addEventListener('click', () => {
          toggler.classList.toggle('active')
          parentContainer.classList.toggle('wide')
          container.removeAttribute('style')
        })
      }
    }
  })();
  // toggle size product config mobile

// bounce config image mobile after change option
(function () {
  const image = document.querySelector('.sticky_config_price .product__config_image--mobile img') || document.querySelector('.product__config_image--mobile_image')
  const options = document.querySelectorAll('.product__info-wrapper [data-name="mats_type"] input')

  if (image) {
    options.forEach(el => {
      el.addEventListener('change', () => {
        image.classList.add('animate__bounce')
        setTimeout(() => {
          
          image.classList.remove('animate__bounce')
        }, 1000);
      })
    })   
  }
})();
// bounce config image mobile after change option




// product sticky price
(function () {
  if (document.querySelector('[data-evamats-config-step-bar]')) return;

  const mainPriceElement = document.querySelector('.main_price');
  const stickyConfigPriceElement = document.querySelector('.sticky_container');

  if (stickyConfigPriceElement && window.innerWidth > 767) {
      // Создаем наблюдатель
      const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
          if (!entry.isIntersecting) {
              // Если элемент не виден, добавляем класс active
              stickyConfigPriceElement.classList.add('active');
          } else {
              // Если элемент виден, убираем класс active
              stickyConfigPriceElement.classList.remove('active');
          }
          });
      });
      
      window.addEventListener('load', () => {
          if (!stickyConfigPriceElement.closest('.evamats-config')) {
            stickyConfigPriceElement.style.width = stickyConfigPriceElement.offsetWidth + 1 + 'px';
          }
      })
      
      // Начинаем наблюдение за основным элементом
      if (mainPriceElement) {
          observer.observe(mainPriceElement);
      }
  } else if (stickyConfigPriceElement) {
    stickyConfigPriceElement.classList.add('active');
  }

})();
// product sticky price


(function () {
  const addToCartButton = document.querySelector('.sticky_add_to_cart');
  if (!addToCartButton) return;

  addToCartButton.addEventListener('click', function () {

    const target = document.querySelector('#uwagi-do-zamowienia');
    if (target) {
        const rect = target.getBoundingClientRect();
        const offset = window.pageYOffset + rect.top - 300; // -300 пикселей вверх
        window.scrollTo({
            top: offset,
            behavior: 'smooth'
        });
    }
    const submitButton = document.querySelector('.product-form__submit');
    if (submitButton) submitButton.click();
  })
})();

// product tabs
(function () {
  document.addEventListener("DOMContentLoaded", function () {
    const root = document.querySelector(".evamats-product-tabs");
    if (!root) return;

    const mobileMq = window.matchMedia("(max-width: 749px)");
    const tabs = root.querySelectorAll(".product_tabs__item");
    const containers = Array.from(root.querySelectorAll(".product_tabs__containers_item"));
    const panelsHost = root.querySelector("[data-tab-panels]");
    const mobileTriggers = root.querySelectorAll(".evamats-product-tabs__mobile-trigger");
    const descriptionSections = document.querySelectorAll(".image-with-text--description");
    const panelSlots = root.querySelectorAll("[data-mobile-panel-slot]");
    const introRoots = root.querySelectorAll("[data-intro-collapse]");
    const isGiftCardTabs = root.hasAttribute("data-gift-card-tabs");

    const panelHome = new Map();

    containers.forEach((panel, index) => {
      panelHome.set(panel, {
        parent: panelsHost,
        index,
      });
    });

    function isMobileLayout() {
      return mobileMq.matches;
    }

    function isAlwaysOpenAboutPanel(panel) {
      return panel && panel.dataset.id === "description" && !isGiftCardTabs;
    }

    function restorePanelsToDesktop() {
      [...containers]
        .sort((a, b) => panelHome.get(a).index - panelHome.get(b).index)
        .forEach((panel) => {
          panel.classList.remove(
            "evamats-product-tabs__panel--mobile-inline",
            "evamats-product-tabs__panel--mobile-about",
            "active"
          );
          panelsHost.appendChild(panel);
        });

      panelSlots.forEach((slot) => {
        slot.hidden = true;
        slot.innerHTML = "";
      });
    }

    function layoutMobilePanels() {
      restorePanelsToDesktop();

      if (!isMobileLayout()) return;

      const aboutPanel = root.querySelector('.product_tabs__containers_item[data-id="description"]');
      if (isAlwaysOpenAboutPanel(aboutPanel)) {
        aboutPanel.classList.add("active", "evamats-product-tabs__panel--mobile-about");
      }

      containers.forEach((panel) => {
        if (isAlwaysOpenAboutPanel(panel)) return;

        const slot = root.querySelector(`[data-mobile-panel-slot="${panel.dataset.id}"]`);
        if (!slot) return;

        panel.classList.add("evamats-product-tabs__panel--mobile-inline");
        slot.appendChild(panel);
      });
    }

    function closeMobileAccordions() {
      mobileTriggers.forEach((trigger) => {
        trigger.classList.remove("is-open");
        trigger.setAttribute("aria-expanded", "false");
      });

      containers.forEach((panel) => {
        if (isAlwaysOpenAboutPanel(panel)) return;
        panel.classList.remove("active");
      });

      panelSlots.forEach((slot) => {
        slot.hidden = true;
      });
    }

    function switchTab(activeId) {
      if (isMobileLayout()) return;

      tabs.forEach((tab) => {
        tab.classList.remove("active");
        tab.setAttribute("aria-selected", "false");
      });
      containers.forEach((container) => container.classList.remove("active"));

      const activeTab = root.querySelector(`.product_tabs__item[data-id="${activeId}"]`);
      const activeContainer = root.querySelector(`.product_tabs__containers_item[data-id="${activeId}"]`);

      if (activeTab) {
        activeTab.classList.add("active");
        activeTab.setAttribute("aria-selected", "true");
      }
      if (activeContainer) activeContainer.classList.add("active");

      descriptionSections.forEach((section) => {
        section.style.display = activeId === "description" ? "" : "none";
      });
    }

    function toggleMobileAccordion(trigger) {
      const activeId = trigger.getAttribute("data-id");
      const isOpen = trigger.classList.contains("is-open");
      closeMobileAccordions();

      if (!isOpen) {
        trigger.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");

        const activeContainer = root.querySelector(`.product_tabs__containers_item[data-id="${activeId}"]`);
        if (activeContainer) activeContainer.classList.add("active");

        const activeSlot = root.querySelector(`[data-mobile-panel-slot="${activeId}"]`);
        if (activeSlot) activeSlot.hidden = false;
      }
    }

    function openGiftCardDescriptionAccordion() {
      if (!isGiftCardTabs) return;
      const descTrigger = root.querySelector(
        '.evamats-product-tabs__mobile-trigger[data-id="description"]'
      );
      if (!descTrigger || descTrigger.classList.contains("is-open")) return;
      toggleMobileAccordion(descTrigger);
    }

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        switchTab(tab.getAttribute("data-id"));
      });
    });

    mobileTriggers.forEach((trigger) => {
      trigger.addEventListener("click", () => {
        toggleMobileAccordion(trigger);
      });
    });

    introRoots.forEach((introRoot) => {
      const toggle = introRoot.querySelector("[data-intro-toggle]");
      const introText = introRoot.querySelector("[data-intro-text]");
      if (!toggle || !introText) return;

      const readMore = toggle.querySelector("span:first-child")?.textContent?.trim() || (window.EVAMATS_I18N || {}).readMore || "";
      const readLess = toggle.getAttribute("data-read-less") || readMore;

      toggle.addEventListener("click", () => {
        const expanded = introRoot.classList.toggle("is-expanded");
        toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
        const label = toggle.querySelector("span:first-child");
        if (label) label.textContent = expanded ? readLess : readMore;
      });
    });

    function syncLayout() {
      layoutMobilePanels();

      if (isMobileLayout()) {
        closeMobileAccordions();
        openGiftCardDescriptionAccordion();
        descriptionSections.forEach((section) => {
          section.style.display = "";
        });
        return;
      }

      root.classList.remove("evamats-product-tabs--mobile");
      if (isGiftCardTabs) {
        switchTab("description");
      } else {
        const firstTab = tabs[0];
        if (firstTab) switchTab(firstTab.getAttribute("data-id"));
      }
    }

    syncLayout();

    if (typeof mobileMq.addEventListener === "function") {
      mobileMq.addEventListener("change", syncLayout);
    } else if (typeof mobileMq.addListener === "function") {
      mobileMq.addListener(syncLayout);
    }
  });
})();
// product tabs

// product gallery Fancybox — load on first click
(function () {
  var PRODUCT_GROUPS = {
    main: true,
    with_edges: true,
    without_edges: true,
  };
  var bound = false;
  var opening = false;

  function bindProductGroups() {
    if (bound || typeof Fancybox === 'undefined') return;
    Fancybox.bind('[data-fancybox="main"]', {});
    Fancybox.bind('[data-fancybox="with_edges"]', {});
    Fancybox.bind('[data-fancybox="without_edges"]', {});
    bound = true;
  }

  function openGroup(link) {
    var group = link.getAttribute('data-fancybox');
    var items = Array.prototype.slice.call(
      document.querySelectorAll('a[data-fancybox="' + group + '"]')
    );
    var startIndex = Math.max(0, items.indexOf(link));
    var slides = items.map(function (el) {
      return { src: el.getAttribute('href'), type: 'image' };
    });
    Fancybox.show(slides, { startIndex: startIndex });
  }

  document.addEventListener(
    'click',
    function (event) {
      var link = event.target.closest('a[data-fancybox]');
      if (!link) return;
      var group = link.getAttribute('data-fancybox');
      if (!PRODUCT_GROUPS[group]) return;
      if (opening) return;

      if (bound && typeof Fancybox !== 'undefined') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      opening = true;

      var loader =
        typeof window.ensureFancyboxLoaded === 'function'
          ? window.ensureFancyboxLoaded()
          : Promise.reject(new Error('ensureFancyboxLoaded missing'));

      loader
        .then(function () {
          bindProductGroups();
          openGroup(link);
        })
        .catch(function (err) {
          console.error(err);
          window.location.href = link.href;
        })
        .finally(function () {
          opening = false;
        });
    },
    true
  );
})();
